import React, { useState, useEffect } from 'react';
import '../styles/design-system.css';

// Logo URLs
// Logo assets - stored in /public/assets/
const LOGO_ICON_BLUE = '/assets/logo-icon-blue.png';
const LOGO_ICON_WHITE = '/assets/logo-icon-white.png';
const LOGO_FULL_BLUE = '/assets/logo-full-blue.png';
const LOGO_FULL_WHITE = '/assets/logo-full-white.png';

export default function SplashScreen({ onComplete }) {
  const [stage, setStage] = useState('logo'); // logo -> loading -> complete
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    // Stage 1: Show logo animation
    const logoTimer = setTimeout(() => {
      setStage('loading');
    }, 1500);

    return () => clearTimeout(logoTimer);
  }, []);

  useEffect(() => {
    if (stage === 'loading') {
      // Animate progress bar
      const progressInterval = setInterval(() => {
        setProgress(prev => {
          if (prev >= 100) {
            clearInterval(progressInterval);
            setTimeout(() => {
              setStage('complete');
              if (onComplete) onComplete();
            }, 300);
            return 100;
          }
          return prev + 5;
        });
      }, 50);

      return () => clearInterval(progressInterval);
    }
  }, [stage, onComplete]);

  if (stage === 'complete') return null;

  return (
    <div 
      className="fixed inset-0 z-[9999] flex flex-col items-center justify-center"
      style={{
        background: 'linear-gradient(135deg, #0A2540 0%, #0D47A1 50%, #1565C0 100%)'
      }}
    >
      {/* Animated background particles */}
      <div className="absolute inset-0 overflow-hidden">
        {[...Array(20)].map((_, i) => (
          <div
            key={i}
            className="absolute rounded-full opacity-10"
            style={{
              width: Math.random() * 100 + 50,
              height: Math.random() * 100 + 50,
              background: 'linear-gradient(135deg, #00BCD4, #1565C0)',
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animation: `logoFloat ${3 + Math.random() * 4}s ease-in-out infinite`,
              animationDelay: `${Math.random() * 2}s`
            }}
          />
        ))}
      </div>

      {/* Logo Container */}
      <div className="relative z-10 flex flex-col items-center">
        {/* Logo Icon with glow effect */}
        <div 
          className={`relative ${stage === 'logo' ? 'animate-logo-scale' : 'animate-float'}`}
          style={{
            filter: 'drop-shadow(0 0 30px rgba(0, 188, 212, 0.5))'
          }}
        >
          <img 
            src={LOGO_ICON_WHITE} 
            alt="Rebuilt" 
            className="w-32 h-32 md:w-40 md:h-40"
          />
        </div>

        {/* Brand Name */}
        <h1 
          className="mt-6 text-4xl md:text-5xl font-extrabold text-white tracking-tight animate-fade-in-up"
          style={{ 
            fontFamily: 'Poppins, sans-serif',
            animationDelay: '0.3s',
            opacity: 0,
            animationFillMode: 'forwards'
          }}
        >
          REBUILT
        </h1>

        {/* Tagline */}
        <p 
          className="mt-2 text-lg md:text-xl text-cyan-300 tracking-widest animate-fade-in-up"
          style={{ 
            fontFamily: 'Inter, sans-serif',
            animationDelay: '0.5s',
            opacity: 0,
            animationFillMode: 'forwards'
          }}
        >
          YOUR EMI PARTNER
        </p>

        {/* Loading Section */}
        {stage === 'loading' && (
          <div className="mt-12 w-64 animate-fade-in">
            {/* Progress Bar */}
            <div className="h-1.5 bg-white/20 rounded-full overflow-hidden">
              <div 
                className="h-full rounded-full transition-all duration-100"
                style={{ 
                  width: `${progress}%`,
                  background: 'linear-gradient(90deg, #00BCD4, #4DD0E1)'
                }}
              />
            </div>
            {/* Loading Text */}
            <p className="mt-4 text-center text-white/70 text-sm">
              {progress < 30 ? 'Initializing...' : progress < 70 ? 'Loading resources...' : 'Almost ready...'}
            </p>
          </div>
        )}
      </div>

      {/* Bottom decoration */}
      <div className="absolute bottom-8 text-white/40 text-xs tracking-wider">
        Powered by Rebuilt Pvt Ltd
      </div>
    </div>
  );
}

// Export logo URLs for use in other components
export { LOGO_ICON_BLUE, LOGO_ICON_WHITE, LOGO_FULL_BLUE, LOGO_FULL_WHITE };
